import api from './api';

/**
 * Get dashboard statistics
 * Aggregates data from multiple endpoints
 * @returns {Promise<Object>} Dashboard stats
 */
export const getDashboardStats = async () => {
  try {
    const [placesRes, journalsRes, feedbackRes] = await Promise.allSettled([
      api.get('/admin/places'),
      api.get('/admin/journals/stats'),
      api.get('/admin/feedback/stats')
    ]);

    const stats = {
      places: { total: 0, published: 0, draft: 0, archived: 0 },
      journals: { total: 0, pending: 0, approved: 0, rejected: 0 },
      feedback: { total: 0, new: 0, read: 0, responded: 0 }
    };

    // Process places
    if (placesRes.status === 'fulfilled') {
      const placesData = placesRes.value.data?.data || placesRes.value.data || [];
      stats.places.total = Array.isArray(placesData) ? placesData.length : 0;
      if (Array.isArray(placesData)) {
        stats.places.published = placesData.filter(p => p.status === 'published').length;
        stats.places.draft = placesData.filter(p => p.status === 'draft').length;
        stats.places.archived = placesData.filter(p => p.status === 'archived').length;
      }
    }

    // Process journals
    if (journalsRes.status === 'fulfilled') {
      const journalsData = journalsRes.value.data?.data || journalsRes.value.data || {};
      stats.journals = {
        total: journalsData.total || 0,
        pending: journalsData.pending || 0,
        approved: journalsData.approved || 0,
        rejected: journalsData.rejected || 0
      };
    }

    // Process feedback
    if (feedbackRes.status === 'fulfilled') {
      const feedbackData = feedbackRes.value.data?.data || feedbackRes.value.data || {};
      stats.feedback = {
        total: feedbackData.total || 0,
        new: feedbackData.new || 0,
        read: feedbackData.read || 0,
        responded: feedbackData.responded || 0
      };
    }

    return stats;
  } catch (error) {
    console.error('Error fetching dashboard stats:', error);
    throw error;
  }
};

/**
 * Get places status distribution
 * @returns {Promise<Array>} Places by status
 */
export const getPlacesStatusStats = async () => {
  try {
    const response = await api.get('/admin/places');
    const places = response.data?.data || response.data || [];
    
    if (!Array.isArray(places)) return [];

    const statusCounts = {
      published: 0,
      draft: 0,
      archived: 0
    };

    places.forEach(place => {
      const status = place.status || 'draft';
      if (statusCounts[status] !== undefined) {
        statusCounts[status]++;
      }
    });

    return [
      { name: 'Published', value: statusCounts.published, color: '#10b981' },
      { name: 'Draft', value: statusCounts.draft, color: '#f59e0b' },
      { name: 'Archived', value: statusCounts.archived, color: '#6b7280' }
    ].filter(item => item.value > 0);
  } catch (error) {
    console.error('Error fetching places stats:', error);
    return [];
  }
};

/**
 * Get journals moderation status distribution
 * @returns {Promise<Array>} Journals by status
 */
export const getJournalStatusStats = async () => {
  try {
    const response = await api.get('/admin/journals/stats');
    const data = response.data?.data || response.data || {};

    return [
      { name: 'Pending', value: data.pending || 0, color: '#f59e0b' },
      { name: 'Approved', value: data.approved || 0, color: '#10b981' },
      { name: 'Rejected', value: data.rejected || 0, color: '#ef4444' }
    ].filter(item => item.value > 0);
  } catch (error) {
    console.error('Error fetching journal stats:', error);
    return [];
  }
};

/**
 * Get feedback status distribution
 * @returns {Promise<Array>} Feedback by status
 */
export const getFeedbackStats = async () => {
  try {
    const response = await api.get('/admin/feedback/stats');
    const data = response.data?.data || response.data || {};

    return [
      { name: 'New', value: data.new || 0, color: '#3b82f6' },
      { name: 'Read', value: data.read || 0, color: '#f59e0b' },
      { name: 'Responded', value: data.responded || 0, color: '#10b981' }
    ].filter(item => item.value > 0);
  } catch (error) {
    console.error('Error fetching feedback stats:', error);
    return [];
  }
};

/**
 * Get categories distribution
 * @returns {Promise<Array>} Categories with place counts
 */
export const getCategoriesStats = async () => {
  try {
    const response = await api.get('/admin/categories');
    const categories = response.data?.data || response.data || [];

    if (!Array.isArray(categories)) return [];

    return categories
      .map(cat => ({
        name: cat.name,
        value: cat.places_count || cat.placesCount || 0,
        color: `hsl(${Math.random() * 360}, 70%, 50%)`
      }))
      .filter(item => item.value > 0)
      .sort((a, b) => b.value - a.value)
      .slice(0, 10);
  } catch (error) {
    console.error('Error fetching categories stats:', error);
    return [];
  }
};

const analyticsService = {
  getDashboardStats,
  getPlacesStatusStats,
  getJournalStatusStats,
  getFeedbackStats,
  getCategoriesStats
};

export default analyticsService;

